'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import {
  getVendorSettings,
  listStorefrontPages,
  listVendorProducts,
  listVendorLinkRequests,
  type VendorSettings,
} from './api';
import { SellReadinessChecklist } from './SellReadinessChecklist';

export interface VendorOverviewPanelProps {
  vendorUserId: string;
}

export function VendorOverviewPanel({ vendorUserId }: VendorOverviewPanelProps) {
  const [settings, setSettings] = useState<VendorSettings | null>(null);
  const [productCount, setProductCount] = useState(0);
  const [activeProducts, setActiveProducts] = useState(0);
  const [storefrontCount, setStorefrontCount] = useState(0);
  const [pendingLinks, setPendingLinks] = useState(0);

  useEffect(() => {
    getVendorSettings(vendorUserId)
      .then(setSettings)
      .catch(() => setSettings(null));
    listVendorProducts(vendorUserId)
      .then((r) => {
        setProductCount(r.products.length);
        setActiveProducts(r.products.filter((p) => p.status === 'active').length);
      })
      .catch(() => undefined);
    listStorefrontPages(vendorUserId)
      .then((r) => setStorefrontCount(r.pages.length))
      .catch(() => undefined);
    listVendorLinkRequests(vendorUserId)
      .then((r) => setPendingLinks(r.requests.filter((req) => req.status === 'pending').length))
      .catch(() => undefined);
  }, [vendorUserId]);

  const hasStoreName = !!settings?.storeName?.trim();

  return (
    <section>
      <h2 className="vendor-console__section-title">Overview</h2>
      <p className="vendor-console__hint">
        Complete the setup checklist to launch your storefront on your public profile.
      </p>
      <SellReadinessChecklist vendorUserId={vendorUserId} />
      <ul className="vendor-console__checklist">
        <li className={hasStoreName ? 'is-done' : undefined}>Set your store name and policies</li>
        <li className={storefrontCount > 0 ? 'is-done' : undefined}>Register at least one external shop URL</li>
        <li className={activeProducts > 0 ? 'is-done' : undefined}>Publish at least one active product</li>
        <li className={pendingLinks === 0 && productCount > 0 ? 'is-done' : undefined}>
          Link community claims to build trust (optional)
        </li>
      </ul>
      <p>
        {settings?.storeName ? (
          <>
            Store: <strong>{settings.storeName}</strong>
          </>
        ) : (
          'Store not configured yet.'
        )}
      </p>
      <p>
        {activeProducts} active / {productCount} total products · {storefrontCount} storefront URLs ·{' '}
        {pendingLinks} pending claim links
      </p>
      <p>
        <Link href={`/marketplace/sellers/${vendorUserId}/`}>View public store</Link>
        {' · '}
        <Link href={`/users/${vendorUserId}/`}>Member profile</Link>
      </p>
    </section>
  );
}
